// 2026-08-19: duplicate-title warning on Add Title, prompted by "Nutty Professor 2"
// getting entered twice. Normalizes both the typed title and every existing title
// (lowercase, strip accents/punctuation, collapse whitespace) and flags anything close
// via Levenshtein-distance similarity -- catches exact-after-normalization duplicates
// (ratio 1.0) as well as near-misses (typos, minor punctuation/wording differences).
// Deliberately a WARNING, not a hard block: legitimate near-duplicates exist (remakes,
// same title/different year) -- see TitleAddPage's duplicateWarnings UI.

const DIACRITIC_MARKS_REGEX = new RegExp("[\\u0300-\\u036f]", "g");

// Lowercase, strip diacritics, strip punctuation down to bare words, collapse whitespace.
// "Amelie!" (with accent) and "amelie" both normalize to "amelie".
export function normalizeTitle(str) {
    if (!str) {
        return '';
    }

    return str
        .normalize('NFD')
        .replace(DIACRITIC_MARKS_REGEX, '') // strip combining diacritical marks (accents)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')     // punctuation/symbols -> space
        .trim()
        .replace(/\s+/g, ' ');           // collapse whitespace
}

// Standard Levenshtein edit distance (single-row DP, no need for anything fancier at
// this string length / collection size).
function levenshteinDistance(a, b) {
    if (a === b) return 0;
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;

    let previousRow = Array.from({ length: b.length + 1 }, (_, i) => i);

    for (let i = 0; i < a.length; i++) {
        const currentRow = [i + 1];
        for (let j = 0; j < b.length; j++) {
            const insertCost = currentRow[j] + 1;
            const deleteCost = previousRow[j + 1] + 1;
            const substituteCost = previousRow[j] + (a[i] === b[j] ? 0 : 1);
            currentRow.push(Math.min(insertCost, deleteCost, substituteCost));
        }
        previousRow = currentRow;
    }

    return previousRow[b.length];
}

// 1.0 = identical, 0.0 = completely different.
export function similarityRatio(a, b) {
    const maxLen = Math.max(a.length, b.length);
    if (maxLen === 0) {
        return 1;
    }
    return 1 - levenshteinDistance(a, b) / maxLen;
}

const SIMILARITY_THRESHOLD = 0.82;
const MAX_RESULTS = 5;

// existingTitles: [{ id, title, release_date (Date|undefined) }, ...] (see useAllTitlesLite)
// newTitle: string typed into Display title
// newYear: number|undefined -- if the Year of Release field has already been filled in
//
// Returns matches sorted by similarity descending, capped to MAX_RESULTS, each as
// { id, title, release_date, ratio, yearsDiffer }. yearsDiffer is true when both years
// are known and differ by more than 1 -- a hint for the UI, not a filter: a same-name
// different-year match is still surfaced (could be a real duplicate with a typo'd year,
// or could be a legitimate remake -- left for the human to judge either way).
export function findSimilarTitles(newTitle, newYear, existingTitles) {
    if (!newTitle || !existingTitles || existingTitles.length === 0) {
        return [];
    }

    const normalizedNew = normalizeTitle(newTitle);
    if (normalizedNew.length === 0) {
        return [];
    }

    const matches = [];
    for (const existing of existingTitles) {
        const normalizedExisting = normalizeTitle(existing.title);
        if (normalizedExisting.length === 0) {
            continue;
        }

        const ratio = similarityRatio(normalizedNew, normalizedExisting);
        if (ratio >= SIMILARITY_THRESHOLD) {
            const existingYear = existing.release_date ? existing.release_date.getFullYear() : undefined;
            const yearsDiffer = (newYear !== undefined && existingYear !== undefined && Math.abs(newYear - existingYear) > 1);

            matches.push({
                id: existing.id,
                title: existing.title,
                year: existingYear,
                ratio,
                yearsDiffer,
            });
        }
    }

    matches.sort((a, b) => b.ratio - a.ratio);
    return matches.slice(0, MAX_RESULTS);
}
